import { useState } from 'react'
import WireframeBanner from './WireframeBanner'
import { PageDesignNote } from './designNotes'
import ModalityPill from '../../components/badges/ModalityPill'
import { getClassifierById, ORGANISATION, PROFILES, TEAMS } from './mockData'
import { Classifier } from './types'
import TabBar from './components/TabBar'
import DetailRow from './components/DetailRow'

// Classifiers live on profiles, so the client grouping is derived from coverage:
// applied to every profile in the organisation, or only to some.
const ORG_WIDE_IDS = ['hate-harassment', 'spam-scams', 'self-harm'] as const

// Classifiers on a subset of profiles, with a human-readable coverage label.
const SCOPED_ITEMS: { id: string; scopeLabel: string }[] = [
    { id: 'competitor-mentions', scopeLabel: '42 of 88 profiles · Acme — Social' },
    { id: 'brand-risk-imagery', scopeLabel: '1 of 88 profiles · @AcmeUK · YouTube' }
]

type SettingsTab = 'Organisation details' | 'Classifier configuration' | 'Profiles' | 'Teams'
const TABS: SettingsTab[] = [
    'Organisation details',
    'Classifier configuration',
    'Profiles',
    'Teams'
]

// A single classifier card — read-only, no prompt, no version, no edit controls.
function ClassifierCard({
    classifier,
    scopeLabel
}: {
    classifier: Classifier
    scopeLabel?: string
}) {
    return (
        <div className="flex items-start gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3.5">
            <div
                aria-hidden="true"
                className="mt-0.5 flex h-[22px] w-[22px] flex-none items-center justify-center rounded-full bg-safe/10 text-safe font-bold text-[13px]"
            >
                ✓
            </div>
            <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-gray-900">{classifier.title}</span>
                    <ModalityPill modality={classifier.modality} />
                </div>
                <p className="mt-0.5 text-[12.5px] leading-relaxed text-gray-500">
                    {classifier.description}
                </p>
                {scopeLabel && <p className="mt-1 text-[11.5px] text-gray-400">{scopeLabel}</p>}
            </div>
        </div>
    )
}

// Organisation details tab — read-only, no markdown SD notes (internal only).
function OrgDetailsTab({ activeClassifierCount }: { activeClassifierCount: number }) {
    return (
        <div className="max-w-lg space-y-4">
            <div className="rounded-lg border border-gray-200 bg-white">
                <div className="border-b border-gray-100 px-4 py-2.5 text-[13px] font-semibold text-gray-900">
                    Organisation details
                </div>
                <div className="divide-y divide-dashed divide-gray-100 px-4 py-1">
                    <DetailRow label="Name">{ORGANISATION.name}</DetailRow>
                    <DetailRow label="Key contact">
                        {ORGANISATION.keyContact.name}
                        <br />
                        <span className="text-xs text-gray-500">
                            {ORGANISATION.keyContact.email}
                        </span>
                    </DetailRow>
                    <DetailRow label="Teams">{ORGANISATION.teamCount}</DetailRow>
                    <DetailRow label="Profiles">{ORGANISATION.profileCount}</DetailRow>
                    <DetailRow label="Active classifiers">{activeClassifierCount}</DetailRow>
                    <DetailRow label="Account manager">Arwen team</DetailRow>
                </div>
            </div>

            {/* Support card — the client's route to requesting a change. */}
            <div className="rounded-xl border border-gray-200 bg-white px-4 py-4">
                <h3 className="mb-1.5 font-sans !font-medium text-[15px] text-gray-900">
                    Need a change?
                </h3>
                <p className="mb-3 text-[12.5px] leading-relaxed text-gray-500">
                    Want to add a classifier, adjust sensitivity, or turn one off? Our team will set
                    it up for you.
                </p>
                <button
                    type="button"
                    className="rounded-lg bg-arwen-600 px-4 py-2.5 text-[13px] font-semibold text-white hover:bg-arwen-700 focus:outline-none focus:ring-2 focus:ring-arwen-500 focus:ring-offset-2"
                >
                    Contact support
                </button>
            </div>
        </div>
    )
}

// Classifier configuration tab — the client's read-only view of what's applied.
function ClassifiersTab({
    orgWide,
    scoped
}: {
    orgWide: Classifier[]
    scoped: { classifier: Classifier; scopeLabel: string }[]
}) {
    return (
        <div className="max-w-2xl">
            <h2 className="mb-2.5 font-sans text-[11.5px] font-bold uppercase tracking-wider text-gray-700">
                Active on all your profiles
            </h2>
            <div className="flex flex-col gap-2.5">
                {orgWide.map((classifier) => (
                    <ClassifierCard key={classifier.id} classifier={classifier} />
                ))}
            </div>

            <h2 className="mb-2.5 mt-5 font-sans text-[11.5px] font-bold uppercase tracking-wider text-gray-700">
                On some of your profiles
            </h2>
            <div className="flex flex-col gap-2.5">
                {scoped.map(({ classifier, scopeLabel }) => (
                    <ClassifierCard
                        key={classifier.id}
                        classifier={classifier}
                        scopeLabel={scopeLabel}
                    />
                ))}
            </div>
        </div>
    )
}

// Profiles tab — read-only list, no links into internal config pages.
function ProfilesTab() {
    return (
        <div className="rounded-lg border border-gray-200 bg-white">
            <div className="border-b border-gray-100 px-4 py-2.5 text-[13px] font-semibold text-gray-900">
                Profiles{' '}
                <span className="ml-1 text-xs font-normal text-gray-500">
                    Showing {PROFILES.length} of {ORGANISATION.profileCount}
                </span>
            </div>
            <table className="w-full border-collapse text-sm">
                <thead>
                    <tr className="border-b border-gray-100 text-left">
                        <th scope="col" className="px-4 pb-2 pt-2 text-[10.5px] font-semibold uppercase tracking-wide text-gray-500">
                            Profile
                        </th>
                        <th scope="col" className="px-4 pb-2 pt-2 text-[10.5px] font-semibold uppercase tracking-wide text-gray-500">
                            Platform
                        </th>
                        <th scope="col" className="px-4 pb-2 pt-2 text-[10.5px] font-semibold uppercase tracking-wide text-gray-500">
                            Team
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                    {PROFILES.map((profile) => (
                        <tr key={profile.id}>
                            <td className="px-4 py-2.5 font-semibold text-gray-900">
                                {profile.name}
                            </td>
                            <td className="px-4 py-2.5 text-gray-700">{profile.platform}</td>
                            <td className="px-4 py-2.5 text-gray-700">{profile.teamName}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

// Teams tab — read-only list.
function TeamsTab() {
    return (
        <div className="rounded-lg border border-gray-200 bg-white">
            <div className="border-b border-gray-100 px-4 py-2.5 text-[13px] font-semibold text-gray-900">
                Teams <span className="ml-1 text-xs font-normal text-gray-500">{TEAMS.length}</span>
            </div>
            <div className="divide-y divide-dashed divide-gray-100 px-4 py-1">
                {TEAMS.map((team) => (
                    <div key={team.id} className="flex items-center justify-between py-2.5">
                        <span className="text-sm font-semibold text-gray-900">{team.name}</span>
                        <span className="text-sm text-gray-500">{team.profileCount} profiles</span>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default function ClientSettingsPage({
    initialTab = 'Classifier configuration'
}: {
    initialTab?: SettingsTab
}) {
    const [activeTab, setActiveTab] = useState<SettingsTab>(initialTab)

    const orgWideClassifiers = ORG_WIDE_IDS.map((id) => getClassifierById(id)).filter(
        (c): c is Classifier => c !== undefined
    )
    const scopedClassifiers = SCOPED_ITEMS.map(({ id, scopeLabel }) => ({
        classifier: getClassifierById(id),
        scopeLabel
    })).filter(
        (item): item is { classifier: Classifier; scopeLabel: string } =>
            item.classifier !== undefined
    )
    const activeClassifierCount = orgWideClassifiers.length + scopedClassifiers.length

    return (
        <div className="flex min-h-screen flex-col bg-gray-50">
            <WireframeBanner />

            <PageDesignNote type="rationale">
                The organisation settings as a client user would see them — a reduced, read-only
                view of the internal configuration, grouped into tabs.
            </PageDesignNote>

            {/* Read-only context strip — signals this is the client perspective */}
            <div className="flex items-center gap-2 border-b border-arwen-100 bg-arwen-50 px-6 py-2 text-xs text-arwen-700">
                Read-only — this is how {ORGANISATION.name} sees their own settings.
            </div>

            {/* Page header */}
            <div className="px-6 pb-2 pt-5">
                <h1 className="font-sans !font-medium text-2xl text-gray-900">
                    Settings
                    <span aria-hidden="true" className="text-arwen-600">
                        .
                    </span>
                </h1>
                <p className="mt-1 text-[13.5px] text-gray-500">
                    Your organisation, profiles and the classifiers applied across your channels.
                </p>
            </div>

            <TabBar tabs={TABS} activeTab={activeTab} onChange={setActiveTab} />

            {/* Tab content */}
            <div className="flex-1 overflow-auto px-6 py-5">
                {activeTab === 'Organisation details' && (
                    <OrgDetailsTab activeClassifierCount={activeClassifierCount} />
                )}
                {activeTab === 'Classifier configuration' && (
                    <ClassifiersTab orgWide={orgWideClassifiers} scoped={scopedClassifiers} />
                )}
                {activeTab === 'Profiles' && <ProfilesTab />}
                {activeTab === 'Teams' && <TeamsTab />}
            </div>
        </div>
    )
}
